import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import { useStripeLoader } from '@/provider/stripeLoader';
import { ArrowLeft } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  fetchAddress,
  fetchPaymentMethods,
  fetchPrices,
  fetchSubscription,
  fetchUsage,
  setPaymentMethodDefault,
} from '@/features/billing/billingThunks';
import { ensureSubscriptionPayment } from '@/features/billing/subscriptionCheckout';
import { useSubmitLock } from '@/hooks/useSubmitLock';
import { fetchWorkspaceById } from '@/features/workspace/workspaceThunks';
import {
  selectAddress,
  selectCheckoutPricesPending,
  selectCheckoutPromoCode,
  selectPaymentMethods,
  selectSubscription,
} from '@/features/billing/billingSlice';
import { canEditCheckoutPromoCode } from '@/lib/checkoutPromoCode';
import { isPriceCurrencyMismatch, resolveCheckoutErrorKey } from '@/lib/subscriptionPaymentResume';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Title from '../ui/Title';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import Loader from '../ui/Loader';
import { Separator } from '../ui/separator';
import CheckoutPromoCode from './CheckoutPromoCode';
import UpgradePaymentMethodSelector from './UpgradePaymentMethodSelector';
import PaymentForm from '../pages/configuration/subscription/PaymentForm';
import BillingAddressForm, { type BillingAddressFormRef } from './BillingAddressForm';
import {
  formatPrice,
  getPlanDisplayName,
  getPricingInfo,
  getPriceByInterval,
  isPartnerAccountOffer,
} from './usageExpiredUtils';
import { formatFirstPaymentDate, getPaymentSummary } from '@/lib/subscriptionPricing';
import type { StripePriceDto } from '@/features/billing/types';
import { toast } from 'react-toastify';
import { Checkbox } from '../ui/checkbox';
import { Link } from 'react-router-dom';

type WorkspaceInfo = {
  name?: string;
};

type UserInfo = {
  pictureUrl?: string;
  firstName?: string;
  lastName?: string;
};

type PaymentSectionProps = {
  prices: StripePriceDto[] | null;
  selectedPlanKey: string | null;
  isYearly: boolean;
  workspaceId: number | undefined;
  workspace: WorkspaceInfo | null;
  user: UserInfo | null;
  onBack: () => void;
  onSuccess: () => void;
};

export default function PaymentSection({
  prices,
  selectedPlanKey,
  isYearly,
  workspaceId,
  workspace,
  user,
  onBack,
  onSuccess,
}: PaymentSectionProps) {
  const { t, i18n } = useTranslation('billing');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const loadStripe = useStripeLoader();
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const subscription = useAppSelector(selectSubscription);
  const address = useAppSelector(selectAddress);
  const workspaceName = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.name);
  const promoCode = useAppSelector(selectCheckoutPromoCode);
  const workspacePromoCode = useAppSelector(
    (s: RootState) => s.workspace.currentWorkspace?.promoCode,
  );
  const pricesPending = useAppSelector(selectCheckoutPricesPending);
  const promoCodeChecking = useAppSelector((s: RootState) => s.billing.promoCode.loading);
  const isPromoCodeEditable = canEditCheckoutPromoCode({
    isPartnerOffer: isPartnerAccountOffer(prices),
    subscriptionStatus: subscription?.workspaceStatus,
  });

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  // Le chemin reprise ne passe pas par le thunk de création : le verrou anti double clic est local.
  const { isLocked: isPaying, runExclusive } = useSubmitLock();
  const [useNewCard, setUseNewCard] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const billingAddressFormRef = useRef<BillingAddressFormRef>(null);
  const [isBillingAddressValid, setIsBillingAddressValid] = useState(false);

  const selectedPrice =
    selectedPlanKey && prices
      ? getPriceByInterval(prices, selectedPlanKey, isYearly ? 'year' : 'month')
      : null;
  const priceId = selectedPrice?.id ?? null;

  const amount =
    selectedPlanKey && prices
      ? (() => {
          const info = getPricingInfo(prices, selectedPlanKey);
          if (!info) return null;
          return isYearly ? info.yearlyAmount : info.monthlyAmount;
        })()
      : null;

  const paymentSummary = selectedPrice
    ? getPaymentSummary(selectedPrice, { workspacePromoCode: promoCode })
    : null;
  const hasDiscount = paymentSummary?.hasDiscount ?? false;
  // Le récapitulatif annonce un tarif « facturé mensuellement » : il doit porter le montant reconduit.
  const hasRecurringDiscount = paymentSummary?.hasRecurringDiscount ?? false;
  const recurringAmount = paymentSummary?.renewalCents ?? selectedPrice?.amount ?? 0;
  const showTrialNotice = paymentSummary?.showTrialNotice ?? false;
  const promoDeductionCents = paymentSummary?.promoDeductionCents ?? 0;
  const promoCodeLabel = paymentSummary?.promoCodeLabel ?? null;
  const showPromoDeductionLine = promoDeductionCents > 0;

  const displayAmount = paymentSummary ? paymentSummary.dueTodayCents : amount;
  const firstPaymentDateLabel = formatFirstPaymentDate(
    paymentSummary?.firstPaymentDate ?? null,
    i18n.language,
  );
  // Reste à couvrir pour que le détail tombe bien sur le total, que la ligne code promo soit affichée ou non.
  const trialDeductionCents =
    showTrialNotice && selectedPrice && paymentSummary
      ? Math.max(0, selectedPrice.amount - promoDeductionCents - paymentSummary.dueTodayCents)
      : 0;

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchPaymentMethods(workspaceId));
      dispatch(fetchAddress(workspaceId));
    }
  }, [workspaceId, dispatch]);

  useEffect(() => {
    if (paymentMethods?.length) {
      const defaultCard = paymentMethods.find((pm) => pm.isDefault);
      setSelectedPaymentMethodId(defaultCard?.id ?? paymentMethods[0]?.id ?? null);
      setUseNewCard(false);
    } else {
      setSelectedPaymentMethodId(null);
      setUseNewCard(true);
    }
  }, [paymentMethods]);

  const payWithSavedCard = async () => {
    if (!workspaceId || !priceId || !termsAccepted) return;
    const saved = await billingAddressFormRef.current?.saveAddress();
    if (saved === false) return;
    try {
      const pmToUse = selectedPaymentMethodId ?? paymentMethods?.find((pm) => pm.isDefault)?.id;
      if (pmToUse) {
        await dispatch(setPaymentMethodDefault({ workspaceId, paymentMethodId: pmToUse })).unwrap();
      }

      const hasValidAddress =
        address?.companyName &&
        address?.line1 &&
        address?.city &&
        address?.postalCode &&
        address?.country;

      const billingAddress = hasValidAddress
        ? {
            companyName: address!.companyName!,
            line1: address!.line1!,
            line2: address?.line2 ?? undefined,
            city: address!.city!,
            postalCode: address!.postalCode!,
            country: address!.country!,
            taxId: address?.taxId ?? undefined,
          }
        : undefined;

      const outcome = await ensureSubscriptionPayment({
        dispatch,
        workspaceId,
        input: {
          priceId,
          customerName: address?.companyName ?? workspaceName ?? undefined,
          billingAddress: billingAddress ?? undefined,
          promoCode: promoCode ?? undefined,
          termsAccepted,
        },
        currentStatus: subscription?.workspaceStatus,
        pendingPlan: subscription,
        requestedPlan: selectedPrice,
        pendingPromoCode: workspacePromoCode,
      });

      if (outcome.kind === 'hostedInvoice') {
        window.location.assign(outcome.url);
        return;
      }

      if (outcome.kind === 'confirm') {
        const stripeInstance = stripe ?? (await loadStripe());
        if (!stripeInstance || !pmToUse) {
          toast.error(t('paymentSection.toast.paymentFailed'));
          return;
        }
        const { error: paymentError } = await stripeInstance.confirmCardPayment(
          outcome.clientSecret,
          {
            payment_method: pmToUse,
          },
        );
        if (paymentError) {
          toast.error(paymentError.message ?? t('paymentSection.toast.paymentFailed'));
          return;
        }
      }

      await dispatch(fetchSubscription(workspaceId)).unwrap();
      await Promise.all([
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchPaymentMethods(workspaceId)),
        dispatch(fetchWorkspaceById(workspaceId)),
      ]);
      toast.success(t('paymentSection.toast.subscriptionCreated'));
      onSuccess();
    } catch (err: unknown) {
      if (isPriceCurrencyMismatch(err)) {
        void dispatch(fetchWorkspaceById(workspaceId));
        void dispatch(fetchPrices({ workspaceId, ...(promoCode && { promoCode }) }));
      }
      const errorKey = resolveCheckoutErrorKey(err);
      toast.error(errorKey ? tCommon(errorKey) : t('paymentSection.toast.subscriptionError'));
    }
  };

  const handlePayWithSavedCard = () => void runExclusive(payWithSavedCard);

  const handlePaymentFormSuccess = () => onSuccess();

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center p-1 cursor-pointer"
            aria-label={t('paymentSection.backAriaLabel')}
          >
            <ArrowLeft className="size-6" />
          </button>
          <span>{t('paymentSection.titlePayment')}</span>
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-8">
        {/* Workspace */}
        <div className="p-4 items-center rounded-[20px] bg-[#1F1F1F]/3 flex gap-4">
          <Avatar className="size-12">
            <AvatarImage src={user?.pictureUrl} />
            <AvatarFallback>
              {user?.firstName?.charAt(0) ?? ''}
              {user?.lastName?.charAt(0) ?? ''}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xl font-medium">{workspace?.name}</p>
            <p className="text-secondary text-base">
              {t('common.plan')} : <span className="text-highlighted">{selectedPlanKey}</span>
            </p>
            {selectedPlanKey && (
              <p className="text-secondary text-sm flex items-center gap-2">
                {pricesPending ? (
                  <>
                    <Loader />
                    {t('paymentSection.loadingPrices')}
                  </>
                ) : selectedPrice ? (
                  <>
                    {hasRecurringDiscount ? (
                      <>
                        <span className="line-through text-secondary/80 mr-1">
                          {formatPrice(selectedPrice.amount, selectedPrice.currency)}
                        </span>
                        <span className="text-black font-semibold">
                          {formatPrice(recurringAmount, selectedPrice.currency)}
                        </span>
                      </>
                    ) : (
                      <span className="text-black font-semibold">
                        {formatPrice(selectedPrice.amount, selectedPrice.currency)}
                      </span>
                    )}{' '}
                    {isYearly
                      ? t('paymentSection.billedYearly')
                      : t('paymentSection.billedMonthly')}
                  </>
                ) : (
                  t('paymentSection.loadingPrices')
                )}
              </p>
            )}
          </div>
        </div>

        <Separator />

        <CheckoutPromoCode
          workspaceId={workspaceId}
          activeCode={promoCode}
          editable={isPromoCodeEditable}
        />

        {selectedPlanKey && (
          <div className="space-y-2">
            {pricesPending ? (
              <p className="text-secondary text-sm flex items-center gap-2">
                <Loader />
                {t('paymentSection.loadingPrices')}
              </p>
            ) : selectedPrice ? (
              <>
                <div className="flex justify-between">
                  <p className="text-secondary text-sm">
                    {t('paymentSection.monthsPlan', {
                      count: isYearly ? 12 : 1,
                      planKey: getPlanDisplayName(selectedPlanKey),
                    })}
                  </p>
                  <p className="text-secondary text-sm">
                    {formatPrice(selectedPrice.amount, selectedPrice.currency)}
                  </p>
                </div>
                {showPromoDeductionLine ? (
                  <div className="flex justify-between">
                    <p className="text-secondary text-sm">
                      {t('promoCode.applied')}{' '}
                      {promoCodeLabel ? (
                        <span className="font-mono text-secondary">({promoCodeLabel})</span>
                      ) : null}
                    </p>
                    <p className="text-secondary text-sm">
                      -{formatPrice(promoDeductionCents, selectedPrice.currency)}
                    </p>
                  </div>
                ) : null}
                {trialDeductionCents > 0 && (
                  <div className="flex justify-between">
                    <p className="text-secondary text-sm">{t('trialOffer.firstMonthFree')}</p>
                    <p className="text-secondary text-sm">
                      -{formatPrice(trialDeductionCents, selectedPrice.currency)}
                    </p>
                  </div>
                )}
                <div className="flex justify-between">
                  <p className="text-black text-xl font-medium">{t('paymentSection.totalTTC')}</p>
                  <p className="text-black text-xl font-medium">
                    {formatPrice(displayAmount ?? selectedPrice.amount, selectedPrice.currency)}
                  </p>
                </div>
                {showTrialNotice && firstPaymentDateLabel && (
                  <p className="text-secondary text-sm">
                    {t('trialOffer.firstPaymentNotice', {
                      amount: formatPrice(
                        paymentSummary!.firstPaymentCents,
                        selectedPrice.currency,
                      ),
                      date: firstPaymentDateLabel,
                    })}
                  </p>
                )}
              </>
            ) : (
              <p className="text-secondary text-sm">{t('paymentSection.loadingPrices')}</p>
            )}
          </div>
        )}

        <div className="space-y-4">
          <Title level={2}>{t('paymentSection.billingAddress')}</Title>
          <BillingAddressForm
            ref={billingAddressFormRef}
            workspaceId={workspaceId}
            embedded
            onValidityChange={setIsBillingAddressValid}
          />
        </div>

        <div className="space-y-6">
          <Title level={2}>{t('common.paymentByCard')}</Title>

          {paymentMethods && paymentMethods.length > 0 && !useNewCard && (
            <>
              <UpgradePaymentMethodSelector
                paymentMethods={paymentMethods}
                selectedId={selectedPaymentMethodId}
                onSelect={setSelectedPaymentMethodId}
                disabled={isPaying}
              />

              <div className="text-sm text-secondary flex items-center gap-2">
                <Checkbox
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                />
                <div>
                  {t('paymentSection.terms.text')}{' '}
                  <Link
                    className="text-highlighted cursor-pointer underline"
                    to="https://www.welya.io/cgu"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('paymentSection.terms.link')}
                  </Link>
                </div>
              </div>

              <Button
                onClick={handlePayWithSavedCard}
                disabled={
                  isPaying ||
                  pricesPending ||
                  promoCodeChecking ||
                  !priceId ||
                  !termsAccepted ||
                  !isBillingAddressValid
                }
                className="w-full"
              >
                {isPaying ? (
                  <>
                    <Loader />
                    {t('paymentSection.button.processing')}
                  </>
                ) : displayAmount != null && selectedPrice ? (
                  showTrialNotice ? (
                    t('unpaid.payAmount', {
                      amount: formatPrice(displayAmount, selectedPrice.currency),
                    })
                  ) : hasDiscount ? (
                    <>
                      {t('paymentSection.button.pay')}{' '}
                      <span className="line-through opacity-80">
                        {formatPrice(selectedPrice.amount, selectedPrice.currency)}
                      </span>{' '}
                      {formatPrice(selectedPrice.discountedAmount, selectedPrice.currency)}
                    </>
                  ) : (
                    t('unpaid.payAmount', {
                      amount: formatPrice(displayAmount, selectedPrice.currency),
                    })
                  )
                ) : (
                  t('paymentSection.button.pay')
                )}
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">{t('common.or')}</span>
                </div>
              </div>
            </>
          )}

          <div className="space-y-4">
            {useNewCard && paymentMethods && paymentMethods.length > 0 && (
              <button
                type="button"
                className="text-sm text-highlighted cursor-pointer"
                onClick={() => setUseNewCard(false)}
              >
                {t('common.useSavedCard')}
              </button>
            )}

            <div>
              {useNewCard || !paymentMethods?.length ? (
                <PaymentForm
                  amount={displayAmount ?? amount ?? undefined}
                  priceId={priceId}
                  workspaceId={workspaceId}
                  termsAccepted={termsAccepted}
                  onTermsAcceptedChange={setTermsAccepted}
                  onSuccess={handlePaymentFormSuccess}
                  onBeforePayment={async () => {
                    const ok = await billingAddressFormRef.current?.saveAddress();
                    return ok ?? true;
                  }}
                  promoCode={promoCode}
                  disabled={!isBillingAddressValid || pricesPending || promoCodeChecking}
                />
              ) : (
                <Button variant="outline" className="w-full" onClick={() => setUseNewCard(true)}>
                  {t('common.payWithNewCard')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
